import { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import Overlay from "../../../Componentes/Overlay";
import Modal from "../../../Componentes/Utils/Modal";
import SmallLoading from "../../../Componentes/Utils/SmallLoading";
import { useModal } from "../../../contexts/ModalContext";
import { useAmbienteMedicao } from "../../../contexts/AmbienteMedicaoContext";
import useApi from "../../../hooks/useApi";
import { formatBRLFromCents } from "../../../Funcoes/Money";

const Titulo = styled.h3`
    margin: 0 0 12px 0;
    font-size: 1rem;
    font-weight: 600;
    color: #242d34;
`;

const Subtitulo = styled.p`
    margin: 0 0 16px 0;
    font-size: 13px;
    color: #4b5563;
    line-height: 1.45;
`;

const TabelaScroll = styled.div`
    min-width: 0;
    max-height: min(58vh, 480px);
    overflow: auto;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
`;

const Tabela = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    color: #333;
    thead {
        background-color: #f0f0f0;
        position: sticky;
        top: 0;
        z-index: 1;
    }
    th {
        text-align: left;
        padding: 10px 12px;
        font-weight: 600;
        color: #242d34;
    }
    td {
        padding: 10px 12px;
        border-top: 1px solid #e5e7eb;
        vertical-align: middle;
    }
    tbody tr:nth-child(even) {
        background-color: #fafafa;
    }
    td.colValor {
        white-space: nowrap;
    }
`;

const BotaoLink = styled.button`
    background: none;
    border: none;
    color: #007bff;
    cursor: pointer;
    font-size: 12px;
    padding: 4px 0;
    text-decoration: underline;
    &:hover {
        color: #0056b3;
    }
`;

const Vazio = styled.p`
    margin: 24px 0;
    text-align: center;
    color: #6b7280;
    font-size: 14px;
`;

const ErroBox = styled.p`
    margin: 12px 0 0 0;
    color: #b91c1c;
    font-size: 13px;
`;

export default function ModalNfPendentesBM() {
    const { closeModal } = useModal();
    const { setAmbiente } = useAmbienteMedicao();
    const navigate = useNavigate();
    const api = useApi();

    const [loading, setLoading] = useState(true);
    const [pendentes, setPendentes] = useState([]);
    const [erro, setErro] = useState(null);

    useEffect(() => {
        let cancel = false;

        const carregar = async () => {
            setLoading(true);
            setErro(null);
            try {
                const { data } = await api.get(
                    "/Painel/API/ControleMedicao/ListNfPendentesBM/index.php"
                );
                if (!cancel) {
                    setPendentes(Array.isArray(data?.pendentes) ? data.pendentes : []);
                }
            } catch (e) {
                if (!cancel) {
                    setErro(
                        e?.response?.data?.message ||
                            "Não foi possível carregar a lista de NFs pendentes."
                    );
                    setPendentes([]);
                }
            } finally {
                if (!cancel) setLoading(false);
            }
        };

        carregar();
        return () => {
            cancel = true;
        };
        // Montagem única: useApi recria o client a cada render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const irParaBoletins = (row) => {
        setAmbiente({ id: row.id_obra, descricao: row.obra || "" });
        closeModal();
        navigate(`/modulo/controle-medicao/pedidos-compra/${row.id_pc}/boletins`);
    };

    return (
        <Overlay state={true}>
            <Modal header close={closeModal} size="xl">
                <Titulo>NFs pendentes por boletim (pedido normal)</Titulo>
                <Subtitulo>
                    Pedidos em que o valor das NFs lançadas ainda não cobre o valor
                    do boletim de medição (falta emitir ou complementar NF).
                </Subtitulo>
                {loading ? (
                    <SmallLoading />
                ) : erro ? (
                    <ErroBox>{erro}</ErroBox>
                ) : pendentes.length === 0 ? (
                    <Vazio>Nenhum BM com NF pendente no momento.</Vazio>
                ) : (
                    <TabelaScroll>
                        <Tabela>
                            <thead>
                                <tr>
                                    <th>Obra</th>
                                    <th>PC Nº</th>
                                    <th>BM Nº</th>
                                    <th className="colValor">Valor BM</th>
                                    <th className="colValor">NF lançada</th>
                                    <th className="colValor">Pendente</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {pendentes.map((row) => (
                                    <tr key={row.id_bm}>
                                        <td>{row.obra}</td>
                                        <td>{row.num_pedido}</td>
                                        <td>{row.num_medicao}</td>
                                        <td className="colValor">
                                            {formatBRLFromCents(Number(row.valor_bm) || 0)}
                                        </td>
                                        <td className="colValor">
                                            {formatBRLFromCents(Number(row.valor_nf) || 0)}
                                        </td>
                                        <td className="colValor">
                                            {formatBRLFromCents(
                                                Number(row.valor_pendente_nf) || 0
                                            )}
                                        </td>
                                        <td>
                                            <BotaoLink
                                                type="button"
                                                onClick={() => irParaBoletins(row)}
                                            >
                                                Boletins
                                            </BotaoLink>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Tabela>
                    </TabelaScroll>
                )}
            </Modal>
        </Overlay>
    );
}
